import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { tenantsApi } from '../api/tenants.api';
import type { Tenant } from '../types/tenant.types';
import { ThemePicker } from '@/components/ui/ThemePicker';
import { BrandColorPicker } from '../components/BrandColorPicker';
import { resetTerminologyCache } from '@/lib/useTerminology';
import { ListDefaultsSection } from '../components/ListDefaultsSection';
import { PartnerDefaultsSection } from '../components/PartnerDefaultsSection';
import { MailSettingsSection } from '../components/MailSettingsSection';
import { useAuthStore } from '@/lib/auth.store';
import { erfuelltRecht } from '@symphenia/shared-types';
import { bestaetige } from '@/lib/dialoge';
import { api } from '@/lib/axios';
import {
  Abschnitt,
  FeldZeile,
  feldKlasse,
  mitStand,
  useFeld,
  useSpeicherstelle,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern';

const TIMEZONES = [
  'Europe/Vienna', 'Europe/Berlin', 'Europe/Zurich', 'Europe/London',
  'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'UTC',
];

const PLAN_COLORS: Record<string, string> = {
  FREE: 'bg-secondary text-secondary-foreground',
  PRO: 'bg-primary/10 text-primary',
  ENTERPRISE: 'bg-accent/10 text-accent-foreground',
};

const STATUS_COLORS: Record<string, string> = {
  TRIAL: 'bg-amber-100 text-amber-700',
  ACTIVE: 'bg-green-100 text-green-700',
  PAST_DUE: 'bg-destructive/10 text-destructive',
  CANCELLED: 'bg-muted text-muted-foreground',
};

/**
 * AP-36 Phase D — die Werte der Stelle `tenant`: die Spalten des Mandanten und
 * `settings` **flach** daneben, so wie das PATCH sie nimmt (`timezone`,
 * `partnerDefaults.customer.paymentTermsDays` …). Jeder Schlüssel steht
 * ausdrücklich da: Die Stelle führt Antworten flach zusammen, und ein im Server
 * gelöschter Schlüssel (`primaryColor` zurückgesetzt) muss als `null` ankommen,
 * sonst bliebe der alte Wert als Basis stehen.
 */
export function mandantWerte(a: unknown): Datensatz {
  const t = (a ?? {}) as Datensatz;
  const s = (t.settings ?? {}) as Datensatz;
  return {
    name: t.name,
    defaultLocale: t.defaultLocale,
    defaultTaxRate: t.defaultTaxRate,
    updatedAt: t.updatedAt,
    timezone: s.timezone ?? 'Europe/Vienna',
    logoUrl: s.logoUrl ?? null,
    terminologyMode: s.terminologyMode ?? 'PROJECT',
    primaryColor: s.primaryColor ?? null,
    partnerDefaults: s.partnerDefaults ?? {},
    partnerAddressLabels: s.partnerAddressLabels ?? [],
  };
}

const URL_RE = /^https?:\/\/\S+$/i;

export default function TenantSettingsPage() {
  const { t } = useTranslation();
  // AP-28 4.6: Der Standard-Steuersatz gehört zur Artikelverwaltung.
  const artikelGebucht = useAuthStore().hasModule('ARTICLES');
  // AP-31 Nachtrag: Die Vorgaben für Geschäftspartner gehören zum CRM.
  const crmGebucht = useAuthStore().hasModule('CRM');
  // AP-10 Nachtrag 2: Downloadlinks betreffen heute nur den Belegversand (9.2-8).
  // Der Hook steht einmal und ohne Bedingung — hinter `&&` fiele er je nach Buchung weg.
  const auth = useAuthStore();
  // AP-10 Nachtrag 5: Exporte gehen auch ohne Belegwesen per Mail — die Sektion hängt nur am Recht.
  const mailVerwalten = erfuelltRecht('core.mail.manage', auth.user?.permissions);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);

  /** Löschantrag: eigener Zustand, eine Handlung mit eigenem Knopf. */
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteDone, setDeleteDone] = useState<string | null>(null);

  // AP-36: eine Stelle für den Mandanten — Firmendaten, Farbe und die Partner-Vorgaben
  // schreiben dieselbe Zeile und teilen sich Warteschlange und Stand.
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: 'tenant',
    bezeichnung: 'Mandanten-Einstellungen',
    daten: tenant as unknown as Datensatz | null,
    werte: mandantWerte,
    schreibe: (patch, stand) =>
      api.patch<Datensatz>('/tenants/me', patch, mitStand(stand)).then((r) => {
        // Der Begriff Projekt/Auftrag steckt in einem Cache — nach dem Umschalten neu lesen.
        if ('terminologyMode' in patch) resetTerminologyCache();
        return r.data;
      }),
    keepalive: { url: '/tenants/me', method: 'PATCH' },
  });

  useEffect(() => {
    tenantsApi.getMe().then(({ data }) => {
      setTenant(data);
      setLoading(false);
    });
  }, []);

  const handleDelete = async () => {
    const subdomain = tenant?.subdomain ?? '';
    if (!(await bestaetige(t('tenants.deleteConfirm', { subdomain })))) return;

    setDeleteError(null);
    setDeleteDone(null);
    setDeleting(true);
    try {
      await tenantsApi.deleteMe();
      // Die Antwort des Backends ist fest englisch — die Meldung kommt aus der Übersetzung.
      setDeleteDone(t('tenants.deleteRequested'));
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('common.error');
      setDeleteError(message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">{t('tenants.settings')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{tenant?.subdomain}.symphenia.com</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Theme-Auswahl */}
          <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
            <h2 className="mb-4 text-base font-semibold text-card-foreground">Design</h2>
            <ThemePicker />
            <div className="mt-6 border-t border-border pt-5">
              <Firmenfarbe stelle={stelle as Speicherstelle<unknown>} />
            </div>
          </div>

          <Firmendaten stelle={stelle as Speicherstelle<unknown>} artikelGebucht={artikelGebucht} />

          {/* AP-31 Nachtrag: nur mit gebuchtem CRM (modul-sichtbarkeit.md) */}
          {crmGebucht && <PartnerDefaultsSection stelle={stelle as Speicherstelle<unknown>} />}

          {/* AP-10 Nachtrag 2/5: E-Mail-Versand — eigenes Recht; Downloadlinks nur mit Belegwesen (in der Sektion) */}
          {mailVerwalten && <MailSettingsSection />}

          {/* AP-23 Phase D: Listen-Standards des Mandanten */}
          <ListDefaultsSection />
        </div>

        {/* Sidebar: plan & stats */}
        <div className="space-y-4">
          {/* Subscription card */}
          <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-card-foreground">
              {t('tenants.subscription')}
            </h3>
            {tenant?.subscription ? (
              <>
                <div className="mb-2 flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${PLAN_COLORS[tenant.subscription.plan] ?? ''}`}>
                    {tenant.subscription.plan}
                  </span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[tenant.subscription.status] ?? ''}`}>
                    {tenant.subscription.status}
                  </span>
                </div>
                {tenant.subscription.currentPeriodEnd && (
                  <p className="text-xs text-muted-foreground">
                    {t('tenants.trialEnds')}:{' '}
                    {new Date(tenant.subscription.currentPeriodEnd).toLocaleDateString()}
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">–</p>
            )}
          </div>

          {/* Stats card */}
          <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-card-foreground">
              {t('tenants.stats')}
            </h3>
            <dl className="space-y-2 text-sm">
              {[
                { key: 'nav.users', value: tenant?.stats.users },
                { key: 'nav.clients', value: tenant?.stats.clients },
                { key: 'nav.projects', value: tenant?.stats.projects },
              ].map(({ key, value }) => (
                <div key={key} className="flex justify-between">
                  <dt className="text-muted-foreground">{t(key)}</dt>
                  <dd className="font-medium text-foreground">{value ?? 0}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Danger zone */}
          <div className="rounded-lg border border-destructive/30 bg-card p-5 shadow-sm">
            <h3 className="mb-2 text-sm font-semibold text-destructive">
              {t('tenants.dangerZone')}
            </h3>
            <p className="mb-3 text-xs text-muted-foreground">{t('tenants.deleteIntro')}</p>
            <button
              type="button"
              disabled={deleting}
              className="rounded-md border border-destructive/40 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
              onClick={handleDelete}
            >
              {deleting ? t('common.loading') : t('tenants.deleteAccount')}
            </button>
            {deleteDone && (
              <p className="mt-3 text-xs text-muted-foreground">{deleteDone}</p>
            )}
            {deleteError && (
              <p className="mt-3 text-xs text-destructive">{deleteError}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Firmenfarbe: gespeichert beim Übernehmen im Wähler (AP-36, `BrandColorPicker`). */
function Firmenfarbe({ stelle }: { stelle: Speicherstelle<unknown> }) {
  const { t } = useTranslation();
  const farbe = useFeld<string | null>(stelle, 'primaryColor', { art: 'auswahl', label: t('tenants.primaryColor') });
  return (
    <BrandColorPicker
      savedValue={farbe.gespeichert ?? ''}
      // Leer heißt „zurücksetzen" — der Server entfernt dann den Schlüssel.
      onSave={(hex) => farbe.setze(hex === '' ? null : hex)}
      status={farbe}
    />
  );
}

function Firmendaten({ stelle, artikelGebucht }: { stelle: Speicherstelle<unknown>; artikelGebucht: boolean }) {
  const { t } = useTranslation();
  const name = useFeld(stelle, 'name', { label: t('tenants.companyName'), pflicht: true });
  const sprache = useFeld(stelle, 'defaultLocale', { art: 'auswahl', label: t('tenants.defaultLocale'), pflicht: true });
  const zeitzone = useFeld(stelle, 'timezone', { art: 'auswahl', label: t('tenants.timezone'), pflicht: true });
  const begriff = useFeld(stelle, 'terminologyMode', {
    art: 'auswahl',
    label: t('tenants.terminology', { defaultValue: 'Begriff für Aufträge' }),
    pflicht: true,
  });
  const steuersatz = useFeld<number | null>(stelle, 'defaultTaxRate', {
    art: 'zahl',
    stellen: 2,
    label: t('articles.tenantDefaultTaxRate'),
    pflicht: true,
    pruefe: (w) => (w !== null && (w < 0 || w > 100) ? t('tenants.defaultTaxRateInvalid', { defaultValue: 'Zwischen 0 und 100 %' }) : null),
  });
  const logo = useFeld(stelle, 'logoUrl', {
    label: `${t('tenants.logo')} (URL)`,
    pruefe: (w) => (w && !URL_RE.test(w) ? 'Keine gültige Adresse (https://…)' : null),
  });

  return (
    <Abschnitt titel={t('tenants.companyName')}>
      <FeldZeile label={t('tenants.companyName')} status={name}>
        <input type="text" className={feldKlasse} {...name.props} />
      </FeldZeile>
      <FeldZeile label={t('tenants.defaultLocale')} status={sprache}>
        <select className={feldKlasse} {...sprache.props}>
          <option value="de">Deutsch</option>
          <option value="en">English</option>
        </select>
      </FeldZeile>
      <FeldZeile label={t('tenants.timezone')} status={zeitzone}>
        <select className={feldKlasse} {...zeitzone.props}>
          {TIMEZONES.map((tz) => (
            <option key={tz} value={tz}>
              {tz}
            </option>
          ))}
        </select>
      </FeldZeile>
      <FeldZeile
        label={t('tenants.terminology', { defaultValue: 'Begriff für Aufträge' })}
        status={begriff}
        hilfe={t('tenants.terminologyHint', { defaultValue: 'Bestimmt, ob im gesamten System „Projekt" oder „Auftrag" als Begriff verwendet wird.' })}
      >
        <select className={feldKlasse} {...begriff.props}>
          <option value="PROJECT">{t('tenants.terminologyProject', { defaultValue: 'Projekt (Projektverwaltung, Projektnummer …)' })}</option>
          <option value="ORDER">{t('tenants.terminologyOrder', { defaultValue: 'Auftrag (Auftragsverwaltung, Auftragsnummer …)' })}</option>
        </select>
      </FeldZeile>

      {/* AP-28 4.6 — nur mit gebuchter Artikelverwaltung (modul-sichtbarkeit.md) */}
      {artikelGebucht && (
        <FeldZeile label={t('articles.tenantDefaultTaxRate')} status={steuersatz} hilfe={t('articles.tenantDefaultTaxRateHint')}>
          <input inputMode="decimal" className={`${feldKlasse} w-40`} {...steuersatz.props} />
        </FeldZeile>
      )}

      <FeldZeile label={`${t('tenants.logo')} (URL)`} status={logo}>
        <input type="url" placeholder="https://example.com/logo.png" className={feldKlasse} {...logo.props} />
        {logo.gespeichert && (
          <img
            src={logo.gespeichert}
            alt="Logo preview"
            className="mt-2 h-12 w-auto rounded object-contain"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        )}
      </FeldZeile>
    </Abschnitt>
  );
}
